import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMELINES = ["Within 3 months", "3–6 months", "6–12 months", "Just researching"];
const CITIES = [
  "Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide",
  "Gold Coast", "Canberra", "Hobart", "Other", "Not sure yet",
];
const HOUSEHOLDS = [
  "Just me", "Me + partner", "Family with kids", "Me + pets", "Me + partner + pets",
];
const SITUATIONS = [
  "Have a job lined up",
  "Actively looking for work",
  "Working remotely for NZ employer",
  "Self-employed",
  "Not working right now",
];

// Roman numeral labels i, ii, iii, iv, v, vi, vii, viii
const roman = (n) => ["i", "ii", "iii", "iv", "v", "vi", "vii", "viii"][n - 1] || String(n);

function RN({ n }) {
  return (
    <span className="font-serif-italic text-[var(--ocean)] mr-2 select-none">
      {roman(n)}.
    </span>
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={cn(
        "px-5 py-2 rounded-full border text-sm font-medium transition-colors",
        active
          ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
          : "bg-white text-[var(--ink)] border-[#D6DCE3] hover:border-[var(--ink)]"
      )}
    >
      {children}
    </button>
  );
}

function TextField({ label, n, value, onChange, type = "text", testid, placeholder = "", hint = "" }) {
  return (
    <div>
      <label className="flex items-baseline text-sm font-semibold text-[var(--ink)] mb-2">
        <RN n={n} />
        <span>{label}</span>
      </label>
      {hint && <p className="text-xs text-[var(--ocean)] -mt-1 mb-2 ml-6">{hint}</p>}
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-[#D6DCE3] rounded-xl px-4 py-3 text-[var(--ink)] placeholder:text-[#B8C3CE] focus:outline-none focus:border-[var(--ink)] transition-colors"
      />
    </div>
  );
}

export default function RelocatorIntake() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    move_timeline: "",
    target_city: "",
    who_is_moving: "",
    current_situation: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill from Smart Redirect Funnel — Newsletter popup hands off name+email
  // via URL params (primary) or sessionStorage (fallback). Fires once on mount.
  useEffect(() => {
    let prefill = { email: "", first_name: "" };
    try {
      const params = new URLSearchParams(window.location.search);
      const pEmail = (params.get("email") || "").trim().toLowerCase();
      const pFirst = (params.get("first_name") || "").trim();
      if (pEmail) prefill.email = pEmail;
      if (pFirst) prefill.first_name = pFirst;
      if (!prefill.email && !prefill.first_name) {
        const stashed = sessionStorage.getItem("tdc_prefill");
        if (stashed) {
          const s = JSON.parse(stashed);
          prefill.email = (s.email || "").trim().toLowerCase();
          prefill.first_name = (s.first_name || "").trim();
        }
      }
    } catch (_) {}
    if (prefill.email || prefill.first_name) {
      setForm((f) => ({ ...f, ...prefill }));
      try { sessionStorage.removeItem("tdc_prefill"); } catch (_) {}
    }
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.email.trim() &&
    form.move_timeline &&
    form.target_city &&
    form.who_is_moving &&
    form.current_situation &&
    !submitting;

  async function submit(e) {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Send the full canonical JSON payload with the exact field names the
      // backend RelocatorPayload model expects. Only redirect to Calendly
      // AFTER the ingest endpoint has 2xx'd so we never lose a submission.
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone_number: form.phone_number.trim(),
        move_timeline: form.move_timeline,
        target_city: form.target_city,
        who_is_moving: form.who_is_moving,
        current_situation: form.current_situation,
      };
      const r = await axios.post(`${API}/leads/relocation`, payload);
      const url = r.data?.calendly_url || "https://calendly.com/hello-theditchcollective/discovery";
      toast.success("You're in — off to book your time now.");
      // Small delay so the toast is seen before nav
      setTimeout(() => {
        window.location.href = url;
      }, 700);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went sideways. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries and try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      {/* Top bar — minimal */}
      <header className="border-b border-[#E4E7EA] bg-[var(--paper)]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="font-display font-extrabold text-sm tracking-tight" data-testid="relocator-home-link">
            The Ditch Collective
          </a>
          <a href="/" className="text-xs text-[var(--ocean)] hover:text-[var(--ink)]">
            ← Back to homepage
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14 md:py-24">
        {/* Section label */}
        <p className="font-serif-italic text-[var(--ocean)] text-lg mb-4 tracking-wide" data-testid="relocator-section-label">
          05 — Over to you
        </p>

        {/* Heading — mixed sans + italic serif on "your move" */}
        <h1
          className="font-display text-5xl sm:text-6xl lg:text-[4.5rem] font-extrabold leading-[1.02] tracking-tight"
          data-testid="relocator-heading"
        >
          Tell us about{" "}
          <span className="font-serif-italic font-normal">your move.</span>
        </h1>

        <p className="mt-5 text-[var(--ocean)] text-base md:text-lg max-w-xl">
          Takes 2 minutes. Then pick a time to chat — no waiting.
        </p>

        {/* Form */}
        <form onSubmit={submit} className="mt-12 space-y-8" data-testid="relocator-form">
          {/* First + Last name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextField
              n={1}
              label="First name"
              value={form.first_name}
              onChange={(v) => update("first_name", v)}
              testid="relocator-first-name"
            />
            <TextField
              n={2}
              label="Last name"
              value={form.last_name}
              onChange={(v) => update("last_name", v)}
              testid="relocator-last-name"
            />
          </div>

          {/* Email */}
          <TextField
            n={3}
            label="Email address"
            type="email"
            value={form.email}
            onChange={(v) => update("email", v)}
            testid="relocator-email"
          />

          {/* Phone */}
          <TextField
            n={4}
            label="Phone number"
            value={form.phone_number}
            onChange={(v) => update("phone_number", v)}
            placeholder="+64 21 …"
            hint="NZ or AU number"
            testid="relocator-phone"
          />

          {/* Timeline pills */}
          <div>
            <label className="flex items-baseline text-sm font-semibold text-[var(--ink)] mb-3">
              <RN n={5} />
              <span>When are you planning to move?</span>
            </label>
            <div className="flex flex-wrap gap-2" data-testid="relocator-timeline-group">
              {TIMELINES.map((t) => (
                <Pill
                  key={t}
                  active={form.move_timeline === t}
                  onClick={() => update("move_timeline", t)}
                  testid={`relocator-timeline-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {t}
                </Pill>
              ))}
            </div>
          </div>

          {/* City pills */}
          <div>
            <label className="flex items-baseline text-sm font-semibold text-[var(--ink)] mb-3">
              <RN n={6} />
              <span>Which Australian city?</span>
            </label>
            <div className="flex flex-wrap gap-2" data-testid="relocator-city-group">
              {CITIES.map((c) => (
                <Pill
                  key={c}
                  active={form.target_city === c}
                  onClick={() => update("target_city", c)}
                  testid={`relocator-city-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c}
                </Pill>
              ))}
            </div>
          </div>

          {/* Household pills */}
          <div>
            <label className="flex items-baseline text-sm font-semibold text-[var(--ink)] mb-3">
              <RN n={7} />
              <span>Who's moving with you?</span>
            </label>
            <div className="flex flex-wrap gap-2" data-testid="relocator-household-group">
              {HOUSEHOLDS.map((h) => (
                <Pill
                  key={h}
                  active={form.who_is_moving === h}
                  onClick={() => update("who_is_moving", h)}
                  testid={`relocator-household-${h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {h}
                </Pill>
              ))}
            </div>
          </div>

          {/* Current situation pills */}
          <div>
            <label className="flex items-baseline text-sm font-semibold text-[var(--ink)] mb-3">
              <RN n={8} />
              <span>What best describes your current situation?</span>
            </label>
            <div className="flex flex-wrap gap-2" data-testid="relocator-situation-group">
              {SITUATIONS.map((s) => (
                <Pill
                  key={s}
                  active={form.current_situation === s}
                  onClick={() => update("current_situation", s)}
                  testid={`relocator-situation-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {s}
                </Pill>
              ))}
            </div>
          </div>

          {/* Footnote */}
          <p className="text-sm text-[var(--ocean)] italic max-w-2xl">
            We'll ask the rest (budget, support areas, current city, concerns) on
            the pre-call questionnaire — once you've booked a time.
          </p>

          {/* CTA */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="relocator-submit"
              className={cn(
                "group inline-flex items-center gap-3 px-7 py-4 rounded-full font-display font-extrabold text-base transition-all shadow-sm",
                canSubmit
                  ? "bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--ink-soft)] hover:-translate-y-0.5"
                  : "bg-[#B8C3CE] text-white cursor-not-allowed"
              )}
            >
              <span>{submitting ? "Saving your details…" : "Continue to book my time"}</span>
              <span
                className={cn(
                  "flex items-center justify-center w-9 h-9 rounded-full transition-colors",
                  canSubmit ? "bg-[var(--lime)] text-[var(--ink)] group-hover:bg-[var(--lime-deep)]" : "bg-white/30 text-white"
                )}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              </span>
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
